import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { UniversalSearchModelType } from 'src/app/core/models/universal-search.model';
import { IsLoading, LoadingWrapper } from 'src/app/shared/decorators';
import { DashboardApiService, VisitorIntelApiService } from 'src/app/shared/services/api';
import { AccountService } from 'src/app/shared/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { UserService } from 'src/app/core/services';
import { AddonsIdentifiersEnum, VisitorIntelProviderEnum, DashboardExportsSummaryTypeEnum } from 'src/app/shared/enums';
import {
  DashboardEnrichmentSummaryResponseDto,
  DashboardExportSummaryResponseDto,
  DashboardVisitorIntelAllWeeklyVisitedCounts,
  DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto,
  DashboardVisitorIntelWeeklyExportedCount,
  DashboardVisitorIntelWeeklyExportedResponseDto,
  DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto,
  DashboardVisitorIntelWeeklyVisitedCount,
  DashboardVisitorIntelWeeklySummaryResponseDto,
  DashboardVisitorIntelAgentsResponseDto,
  DashboardNewsAutomationsWeeklyArticlesCount,
  NewsAutomationAgentsCount,
  VisitorIntelResponseInterface,
  WeeklyData,
} from 'src/app/shared/dto';
import { RequestBodyVisitorInsight } from 'src/app/core/models/request-body.model';
import moment from 'moment';
import { User } from 'src/app/core/models/user.model';
import { DashboardCardsEnum } from './enums';
import { animate, style, transition, trigger } from '@angular/animations';

const LOADER_ROD_WEEKLY_SUMMARY: string = 'rod-weekly-summary';
const LOADER_ENRICHMENT_SUMMARY: string = 'enrichment-summary';
const LOADER_EXPORTS_SUMMARY: string = 'exports-summary';
const LOADER_VISITOR_INTEL_SUMMARY: string = 'visitor-intel-summary';
const LOADER_VISITOR_INTEL_DATA: string = 'visitor-intel-data';
const LOADER_NEWS_ALERTS_AGENT_DATA: string = 'news-alerts-data';
const LOADER_VISITOR_INTEL_WEEKLY_AGENT_DATA: string = 'visitor-intel-weely-agent-data';
const LOADER_RUN_VISITOR_INTEL_AUTOMATION: string = 'run-visitor-intel-automation';
const LOADER_VISITOR_INTEL_WEEKLY_EXPORTED_COUNT: string = 'visitor-intel-weekly-exported-count';
const LOADER_NEWS_AUTOMATIONS_WEEKLY_ARTICLES_COUNT: string = 'news-automations-weekly-articles-count';
const LOADER_VISITOR_INTEL_ALL_WEEKLY_VISITED_EXPORTED_COUNT: string = 'visitor-intel-all-weekly-visited-exported-count';


@UntilDestroy()
@Component({
    selector: 'app-reporting-dashboard',
    templateUrl: './reporting-dashboard.component.html',
    styleUrls: ['./reporting-dashboard.component.scss', './common-reporting-dashboard.scss'],
    animations: [
        trigger('fadeOutScale', [
            transition(':leave', [
                animate('300ms ease', style({ opacity: 0, transform: 'scale(0.8)' }))
            ]),
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(1.1)' }),
                animate('300ms ease', style({ opacity: 1, transform: 'scale(1)' }))
            ])
        ])
    ],
    standalone: false
})
export class ReportingDashboardComponent implements OnInit {
  @Input() disableCardSnapshot: { [key in DashboardCardsEnum]?: boolean } = {};
  @Input() visibleCard: { [key in DashboardCardsEnum]: boolean };
  @Input() isEnrichmentEnabled: boolean;
  @Input() showRevDriverCard: boolean;
  @Input() showIntegrationsCard: boolean;
  @Input() showICPCard: boolean;
  @Input() showNewsCard: boolean;
  @Input() showGrowthSignalsCard: boolean;
  @Input() showTechChangeCard: boolean;
  @Input() cardOrder: DashboardCardsEnum[];

  @Output() searchEvent: EventEmitter<UniversalSearchModelType> = new EventEmitter<UniversalSearchModelType>();
  @Output() saveSearchEvent: EventEmitter<Record<string, object>> = new EventEmitter<Record<string, object>>();

  @IsLoading(LOADER_ROD_WEEKLY_SUMMARY)
  isRodWeeklySummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_ENRICHMENT_SUMMARY)
  isEnrichmentSummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_EXPORTS_SUMMARY)
  isExportsSummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_VISITOR_INTEL_SUMMARY)
  isVisitorIntelSummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_VISITOR_INTEL_DATA)
  isVisitorIntelDataLoading$: Observable<boolean>;
  @IsLoading(LOADER_NEWS_ALERTS_AGENT_DATA)
  isNewsAlertsDataLoading$: Observable<boolean>;
  @IsLoading(LOADER_VISITOR_INTEL_WEEKLY_AGENT_DATA)
  isVisitorIntelAgentDataLoading$: Observable<boolean>;
  @IsLoading(LOADER_RUN_VISITOR_INTEL_AUTOMATION)
  isRunVisitorIntelAutomationLoading$: Observable<boolean>;
  @IsLoading(LOADER_VISITOR_INTEL_WEEKLY_EXPORTED_COUNT)
  isVisitorIntelWeeklyExportedCountLoading$: Observable<boolean>;
  @IsLoading(LOADER_NEWS_AUTOMATIONS_WEEKLY_ARTICLES_COUNT)
  isNewsAutomationsWeeklyArticlesCountLoading$: Observable<boolean>;
  @IsLoading(LOADER_VISITOR_INTEL_ALL_WEEKLY_VISITED_EXPORTED_COUNT)
  isVisitorIntelAllWeeklyVisitedExportedCountLoading$: Observable<boolean>;

  isAllAgentsEmailOnly: boolean = false;
  isAllAgentsExportOnly: boolean = false;
  hideNewsCard = environment.hideReportingDashboardNewsCard;
  isAdmin$: Observable<boolean>;
  rodWeeklySummary$: Observable<WeeklyData[]>;
  enrichmentSummary$: Observable<DashboardEnrichmentSummaryResponseDto>;
  exportsSummary$: Observable<DashboardExportSummaryResponseDto>;
  visitorIntelSummary$: Observable<DashboardVisitorIntelWeeklySummaryResponseDto[]>;
  visitorIntelAgents$: Observable<DashboardVisitorIntelAgentsResponseDto[]>;
  isVisitorIntelSetupDone$: Observable<boolean>;
  newsAlertsData$: Observable<NewsAutomationAgentsCount[]>;
  selectedAgent$ = new BehaviorSubject<DashboardVisitorIntelAgentsResponseDto | { isAllAgents: true }>({ isAllAgents: true });
  weeklyAgentData$: Observable<{chartData: number[] | Record<string, number[]>; chartDataLabels: string[]; isWeeklyAgentsNotRun?: boolean; tableData: DashboardVisitorIntelWeeklyExportedCount[] | DashboardVisitorIntelWeeklyVisitedCount[] | DashboardVisitorIntelAllWeeklyVisitedCounts[]; viewAllData?: {universal: boolean, automation_id: number, saved_search_id: number}; }>
allNewsAutomationsWeeklyData$: Observable<{
    articles: DashboardNewsAutomationsWeeklyArticlesCount[],
    agents: NewsAutomationAgentsCount[]
}>;
  selectedNewsAutomationsWeeklyData$: Observable<DashboardNewsAutomationsWeeklyArticlesCount[]>;
  newsArticlesWeeklyAgentData$: Observable<{chartData: number[] | Record<string, number[]>; chartDataLabels: string[]; newsAutomationData: DashboardNewsAutomationsWeeklyArticlesCount[]; }>;
  allVisitorIntelWeeklyExportedData$: Observable<DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto>;

  readonly addonsIdentifiersEnum: typeof AddonsIdentifiersEnum = AddonsIdentifiersEnum;

  readonly dashboardCards = DashboardCardsEnum;

  private exportTypeSubject = new BehaviorSubject<DashboardExportsSummaryTypeEnum | undefined>(undefined);
  private readonly newsArticlesSelectedAgentSubject = new BehaviorSubject<NewsAutomationAgentsCount | null>(null);
  private readonly includeAllUsersSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private readonly includeNewsAllUsersSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly dashboardApiService: DashboardApiService,
    private readonly userService: UserService,
    private readonly visitorIntelApiService: VisitorIntelApiService,
    private readonly toaster: ToastrService,
    private readonly accountService: AccountService
  ) { }

  ngOnInit(): void {
    this.rodWeeklySummary$ = this.includeAllUsersSubject.pipe(
        switchMap((includeAllUsers) => this.getRODWeeklySummary(includeAllUsers))
    );
    this.enrichmentSummary$ = this.getEnrichmentSummary();
    this.exportsSummary$ = this.exportTypeSubject
        .pipe(switchMap((exportType) => this.getExportsSummary(exportType)), untilDestroyed(this));
    this.visitorIntelSummary$ = this.getVisitorIntelWeeklyCompaniesCount().pipe(shareReplay(1));
    this.isAdmin$ = this.userService.listenToUserChangesByKey('is_admin');
    this.visitorIntelAgents$ = this.getVisitorIntelAgentsCount().pipe(shareReplay(1));
    this.isVisitorIntelSetupDone$ = this.userService.getUserData().pipe(
      filter(Boolean),
      switchMap((userData: User) => {
        const date: string = moment().subtract(1, 'days').format('MM/DD/YYYY');
        const requestBodyCombined = {
          ...new RequestBodyVisitorInsight(),
          dateFrom: date,
          dateTo: date,
          uuId: userData.uuid,
        };
        return forkJoin([
          this.getVisitorIntel(
            {
              ...requestBodyCombined,
              page: 1,
            },
            VisitorIntelProviderEnum.BOMBORA,
          ),
          this.getVisitorIntel(
            {
              ...requestBodyCombined,
              page: 1,
            },
            VisitorIntelProviderEnum.FIVE_BY_FIVE,
          ),
        ]);
      }),
      map(results => {
        return results.some(item => !!item?.lastProcessedDate);
      }),
      shareReplay(1),
      catchError(() => of(false))
    );
    this.newsAlertsData$ = this.getNewsAlertsData();
    this.weeklyAgentData();
    this.allNewsAutomationsWeeklyData$ = this.includeNewsAllUsersSubject.pipe(
        switchMap((includeAllUsers) =>
            forkJoin({
                articles: this.getAllNewsArticlesWeeklyData(includeAllUsers),
                agents: this.getNewsAlertsData(includeAllUsers),
            })
        ),
        shareReplay(1),
        untilDestroyed(this)
    )
    this.allVisitorIntelWeeklyExportedData$ = this.getAllVisitorIntelWeeklyVisitedExportedCount().pipe(shareReplay(1));
    this.newsArticlesWeeklyAgentData$ = this.newsArticlesSelectedAgentSubject.pipe(
        filter((val): val is NewsAutomationAgentsCount => !!val),
        switchMap(value => {
          return this.getSelectedAgentNewsArticlesWeeklyData(+value.id).pipe(
            map((data: DashboardNewsAutomationsWeeklyArticlesCount[]) => ({
              chartData: data.map(d => d.count),
              chartDataLabels: data
                .map(d => moment(d.start_date).format('MM/DD/YY'))
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
              newsAutomationData: data.map(d => ({
                ...d,
                start_date: moment(d.start_date, 'YYYY-MM-DD').format('MM/DD/YYYY'),
                end_date: moment(d.end_date, 'YYYY-MM-DD').format('MM/DD/YYYY'),
              }))
            }))
          );
        }),
        shareReplay(1),
        untilDestroyed(this)
      );


  }

    weeklyAgentData() {
        this.weeklyAgentData$ = combineLatest([
            this.selectedAgent$,
            this.visitorIntelAgents$
        ]).pipe(
            filter(([selectedAgent, agentsCount]) => agentsCount.length > 0),
            distinctUntilChanged(
                (prev: [DashboardVisitorIntelAgentsResponseDto | { isAllAgents: true }, any],
                 curr: [DashboardVisitorIntelAgentsResponseDto | { isAllAgents: true }, any]
                ) => {
                    const [prevAgent] = prev;
                    const [currAgent] = curr;

                    const isPrevReal = 'id' in prevAgent;
                    const isCurrReal = 'id' in currAgent;

                    if (isPrevReal && isCurrReal) {
                        return prevAgent.id === currAgent.id;
                    }

                    if (!isPrevReal && !isCurrReal) {
                        return true;
                    }

                    return false;
                }
            ),
            switchMap(([selectedAgent]) => {
                if ('id' in selectedAgent && selectedAgent?.email_only) {
                    return this.getVisitorIntelAgentData(selectedAgent?.id).pipe(
                        map((data: DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto) => {
                            const weeklyResponseSum = data.weekly_visited_counts.reduce((sum, entry) => sum + entry.visited_company_count, 0);
                            const { weekly_visited_counts, ...rest } = data;
                            return {
                                isWeeklyAgentsNotRun: weeklyResponseSum === 0,
                                chartData: data.weekly_visited_counts.map(d => d.visited_company_count),
                                chartDataLabels: data.weekly_visited_counts
                                    .map(d => moment(d.job_date).format('MM/DD/YY')),
                                tableData: data.weekly_visited_counts.map(data => ({ ...data, "job_date": moment(data.job_date).format('MM/DD/YYYY').toString() })),
                                viewAllData: rest
                            };
                        })
                    )
                }
                if ('id' in selectedAgent && !selectedAgent.email_only) {
                    return this.getVisitorIntelWeeklyExportedCount('id' in selectedAgent && selectedAgent?.id).pipe(
                        map((data: DashboardVisitorIntelWeeklyExportedResponseDto) => {
                            const weeklyResponseSum = data.weekly_visited_counts.reduce((sum, entry) => sum + entry.companies_exported_count + entry.contacts_exported_count, 0);
                            const { chartDataLabels, chartData } = data.weekly_visited_counts.reduce(
                                (acc, entry) => {
                                    const weekLabel = moment(entry.job_date).format('MM/DD/YY');
                                    acc.chartDataLabels.push(weekLabel);
                                    acc.chartData.Companies.push(entry.companies_exported_count);
                                    acc.chartData.Contacts.push(entry.contacts_exported_count);
                                    return acc;
                                },
                                { chartDataLabels: [], chartData: { Companies: [], Contacts: [] } }
                            );
                            const { weekly_visited_counts, ...rest } = data;
                            return {
                                isWeeklyAgentsNotRun: weeklyResponseSum === 0,
                                chartDataLabels: chartDataLabels,
                                chartData: chartData,
                                tableData: data.weekly_visited_counts.map(data => ({ ...data, "job_date": moment(data.job_date).format('MM/DD/YYYY').toString() })),
                                viewAllData: rest
                            };
                        })
                    );
                }
                return this.getAllVisitorIntelWeeklyVisitedExportedCount().pipe(
                    map((data: DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto) => {
                        this.isAllAgentsEmailOnly = data.is_email_only;
                        this.isAllAgentsExportOnly =  data.is_export_only;
                        if (!data.is_email_only && !data.is_export_only) {
                            const { chartDataLabels, chartData } = data.weekly_visited_counts.reduce(
                                (acc, entry) => {
                                    const weekLabel = moment(entry.job_date).format('MM/DD/YY');
                                    acc.chartDataLabels.push(weekLabel);
                                    acc.chartData['Companies Exported'].push(entry.companies_exported_count);
                                    acc.chartData['Contacts Exported'].push(entry.contacts_exported_count);
                                    acc.chartData['All Companies'].push(entry.all_companies_count);
                                    return acc;
                                },
                                { chartDataLabels: [], chartData: { 'Companies Exported': [], 'Contacts Exported': [], 'All Companies': [] } }
                            );
                            return {
                                chartDataLabels: chartDataLabels,
                                chartData: chartData,
                                tableData: data.weekly_visited_counts.map(data => ({ ...data, "job_date": moment(data.job_date).format('MM/DD/YYYY').toString() })),
                            };
                        }
                        else if (data.is_export_only) {
                            const { chartDataLabels, chartData } = data.weekly_visited_counts.reduce(
                                (acc, entry) => {
                                    const weekLabel = moment(entry.job_date).format('MM/DD/YY');
                                    acc.chartDataLabels.push(weekLabel);
                                    acc.chartData.Companies.push(entry.companies_exported_count);
                                    acc.chartData.Contacts.push(entry.contacts_exported_count);
                                    return acc;
                                },
                                { chartDataLabels: [], chartData: { Companies: [], Contacts: [] } }
                            );
                            return {
                                chartDataLabels: chartDataLabels,
                                chartData: chartData,
                                tableData: data.weekly_visited_counts.map(data => ({ ...data, "job_date": moment(data.job_date).format('MM/DD/YYYY').toString() })),
                            };
                        } else if (data.is_email_only) {
                            return {
                                chartDataLabels: data.weekly_visited_counts
                                    .map(d => moment(d.job_date).format('MM/DD/YY')),
                                chartData: data.weekly_visited_counts.map(d => d.all_companies_count),
                                tableData: data.weekly_visited_counts.map(data => ({ ...data, "job_date": moment(data.job_date).format('MM/DD/YYYY').toString() })),
                            };
                        }
                    })
                );
            }),
            shareReplay(1),
            untilDestroyed(this)
        );
    }

  onSearch(searchData: UniversalSearchModelType): void {
    this.searchEvent.emit(searchData);
  }

  onSaveSearch(data: Record<string, object>): void {
    this.saveSearchEvent.emit(data);
  }

  onDropdownValueSelect(message?: DashboardExportsSummaryTypeEnum): void {
    this.exportTypeSubject.next(message);
  }

  runVIAutomation(automationId: number): void {
    this.runVisitorIntelAutomation(automationId)
      .pipe(untilDestroyed(this))
      .subscribe(() =>
        this.toaster.success('Success! Your VisitorIntel Automation has run.'),
      );
  }

  updateAgent(value: DashboardVisitorIntelAgentsResponseDto | null) {
    this.selectedAgent$.next(value);
  }

  updateNewsArticleAgent(value: NewsAutomationAgentsCount | null) {
    this.newsArticlesSelectedAgentSubject.next(value);
  }

  loadRODWeeklySummary(includeAllUsers: boolean): void {
    this.includeAllUsersSubject.next(includeAllUsers);
  }

  loadNewsArticlesWeeklySummary(includeAllUsers: boolean): void {
    this.includeNewsAllUsersSubject.next(includeAllUsers);
  }

  @LoadingWrapper(LOADER_ROD_WEEKLY_SUMMARY)
  private getRODWeeklySummary(includeAllUsers: boolean = true): Observable<WeeklyData[]> {
    return this.dashboardApiService.getRODWeeklySummary(includeAllUsers);
  }

  @LoadingWrapper(LOADER_ENRICHMENT_SUMMARY)
  private getEnrichmentSummary(): Observable<DashboardEnrichmentSummaryResponseDto> {
    return this.dashboardApiService.getEnrichmentSummary();
  }

  @LoadingWrapper(LOADER_EXPORTS_SUMMARY)
  private getExportsSummary(
    exportType?: DashboardExportsSummaryTypeEnum,
  ): Observable<DashboardExportSummaryResponseDto> {
    return this.dashboardApiService.getAllExportSummary(exportType);
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_SUMMARY)
  private getVisitorIntelWeeklyCompaniesCount(): Observable<DashboardVisitorIntelWeeklySummaryResponseDto[]> {
    return this.dashboardApiService.getVisitorIntelWeeklyCompaniesCount();
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_SUMMARY)
  private getVisitorIntelAgentsCount(): Observable<DashboardVisitorIntelAgentsResponseDto[]> {
    return this.dashboardApiService.getVisitorIntelAgentsCount();
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_DATA)
  private getVisitorIntel(requestBodyCombined: RequestBodyVisitorInsight, dataSource: VisitorIntelProviderEnum): Observable<VisitorIntelResponseInterface> {
    return this.visitorIntelApiService.getVisitorIntel(requestBodyCombined, dataSource);
  }

  @LoadingWrapper(LOADER_NEWS_ALERTS_AGENT_DATA)
  private getNewsAlertsData(includeAllUsers: boolean = false): Observable<NewsAutomationAgentsCount[]> {
    return this.dashboardApiService.getConfiguredNewsAgents(includeAllUsers);
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_WEEKLY_AGENT_DATA)
  private getVisitorIntelAgentData(automationId: number): Observable<DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto> {
      return this.dashboardApiService.getVisitorIntelWeeklyVisitedCompaniesCount(automationId);
  }

  @LoadingWrapper(LOADER_RUN_VISITOR_INTEL_AUTOMATION)
  runVisitorIntelAutomation(automationId?: number): Observable<null> {
      return this.visitorIntelApiService.runVisitorIntelAutomation({automationId});
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_WEEKLY_EXPORTED_COUNT)
  getVisitorIntelWeeklyExportedCount(automationId?: number): Observable<DashboardVisitorIntelWeeklyExportedResponseDto> {
      return this.dashboardApiService.getVisitorIntelWeeklyExportedCount(automationId)
  }

  @LoadingWrapper(LOADER_NEWS_AUTOMATIONS_WEEKLY_ARTICLES_COUNT)
  private getAllNewsArticlesWeeklyData(includeAllUsers: boolean = true): Observable<DashboardNewsAutomationsWeeklyArticlesCount[]> {
      return this.dashboardApiService.getAllNewsArticlesWeeklyData(includeAllUsers);
  }

  @LoadingWrapper(LOADER_NEWS_AUTOMATIONS_WEEKLY_ARTICLES_COUNT)
  private getSelectedAgentNewsArticlesWeeklyData(automationId?: number): Observable<DashboardNewsAutomationsWeeklyArticlesCount[]> {
      return this.dashboardApiService.getSelectedAgentNewsArticlesWeeklyData(automationId);
  }

  @LoadingWrapper(LOADER_VISITOR_INTEL_ALL_WEEKLY_VISITED_EXPORTED_COUNT)
  private getAllVisitorIntelWeeklyVisitedExportedCount(): Observable<DashboardVisitorIntelAllWeeklyVisitedCountsResponseDto> {
      return this.dashboardApiService.getAllVisitorIntelWeeklyVisitedExportedCount();
  }
}
